import React, { useState, useEffect, useMemo } from "react";
import moment from "moment";
import PropTypes from "prop-types";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Tabs,
  Tab,
  CircularProgress,
  Box,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Icon,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

// Constants
import { MISSIONS, WEEKS, CURRENT_MONTH_NAME, getWeeksForMonth } from "./constants";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const YEARS = Array.from({ length: 5 }, (_, i) => moment().year() - 2 + i);

/**
 * Utility to get default form data with 0 for number fields
 */
const getDefaultFormData = () => {
  const defaults = {};
  MISSIONS.forEach((mission) => {
    mission.metrics.forEach((metric) => {
      if (metric.type === "number") {
        defaults[`${mission.id}_${metric.id}`] = 0;
      }
    });
  });
  return defaults;
};

/**
 * Reusable fetcher for current user profile
 */
const fetchUserProfile = async () => {
  const response = await axios({
    method: "post",
    url: `${process.env.REACT_APP_API_URL}/auth/me`,
    headers: {
      "Content-Type": "application/json",
    },
    withCredentials: true,
  });
  return response.data;
};

/**
 * Reusable Component for an Editable Cell in the table
 */
const EditableCell = ({
  value,
  onChange,
  onBlur,
  error,
  helperText,
  type,
  multiline,
  placeholder,
  readOnly,
  options,
}) => {
  if (type === "dropdown") {
    return (
      <FormControl fullWidth variant="outlined" size="small" error={!!error}>
        <Select
          value={value ?? ""}
          onChange={onChange}
          onBlur={onBlur}
          displayEmpty
          disabled={readOnly}
          sx={{
            borderRadius: "8px",
            backgroundColor: readOnly ? "#f0f2f5" : "#fff",
            height: "40px",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: error ? "#d32f2f" : "#e0e0e0",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: readOnly ? "#e0e0e0" : "#1A73E8",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#1A73E8",
            },
          }}
        >
          <MenuItem value="" disabled>
            <MDTypography variant="caption" color="text">
              Select option...
            </MDTypography>
          </MenuItem>
          {options?.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
        {error && (
          <MDTypography variant="caption" color="error" sx={{ ml: 1, mt: 0.5 }}>
            {error}
          </MDTypography>
        )}
      </FormControl>
    );
  }

  return (
    <TextField
      fullWidth
      variant="outlined"
      size="small"
      multiline={multiline}
      rows={multiline ? 3 : 1}
      value={value ?? ""}
      onChange={onChange}
      onBlur={onBlur}
      error={!!error}
      helperText={error || helperText}
      placeholder={placeholder || (type === "number" ? "0" : "Enter details...")}
      InputProps={{ readOnly: readOnly }}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: "8px",
          backgroundColor: readOnly ? "#f0f2f5" : "#fff",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            backgroundColor: readOnly ? "#f0f2f5" : "#fcfcff",
          },
          "&.Mui-focused": {
            backgroundColor: "#fff",
            boxShadow: "0 0 0 2px rgba(26, 115, 232, 0.2)",
          },
        },
      }}
    />
  );
};

EditableCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  error: PropTypes.string,
  helperText: PropTypes.string,
  type: PropTypes.string,
  multiline: PropTypes.bool,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.string),
};

/**
 * Reusable Component for a Mission Section (Accordion with Metric Rows)
 */
const MissionSection = ({
  mission,
  selectedWeekData,
  onInputChange,
  onBlur,
  errors,
  weekId,
  readOnly,
}) => {
  return (
    <Accordion
      sx={{
        mb: 2,
        borderRadius: "12px !important",
        overflow: "hidden",
        border: "1px solid #e0e0e0",
        boxShadow: "none",
        "&:before": { display: "none" },
      }}
      defaultExpanded={mission.id === "dccm"} // Expand the first one by default
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: mission.color || "#eee",
          minHeight: "64px",
          "& .MuiAccordionSummary-content": {
            margin: "12px 0",
          },
        }}
      >
        <MDTypography variant="h6" fontWeight="bold">
          {mission.name}
        </MDTypography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Table sx={{ minWidth: "100%" }}>
          <TableBody>
            {mission.metrics.map((metric) => {
              // Conditional logic for MPCD: show other fields only if "Applicable" is selected
              if (mission.id === "mpcd" && metric.id !== "is_applicable") {
                const isApplicable = selectedWeekData?.["mpcd_is_applicable"];
                if (isApplicable !== "Applicable") return null;
              }

              return (
                <TableRow key={metric.id} sx={{ "&:hover": { backgroundColor: "#fafafa" } }}>
                  <TableCell sx={{ pl: 4, py: 2, borderBottom: "1px solid #f0f0f0", width: "50%" }}>
                    <MDTypography variant="button" fontWeight="medium" color="dark">
                      {metric.label}
                      {metric.required && (
                        <MDTypography
                          component="span"
                          variant="button"
                          color="error"
                          sx={{ ml: 0.5 }}
                        >
                          *
                        </MDTypography>
                      )}
                    </MDTypography>
                  </TableCell>
                  <TableCell
                    sx={{ py: 1.5, borderBottom: "1px solid #f0f0f0", width: "50%", pr: 4 }}
                  >
                    <EditableCell
                      value={selectedWeekData?.[`${mission.id}_${metric.id}`]}
                      onChange={(e) =>
                        onInputChange(mission.id, metric.id, e.target.value, metric.type)
                      }
                      onBlur={(e) => onBlur(mission.id, metric.id, e.target.value)}
                      error={errors[`${weekId}_${mission.id}_${metric.id}`]}
                      type={metric.type}
                      multiline={metric.multiline}
                      readOnly={readOnly}
                      options={metric.options}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </AccordionDetails>
    </Accordion>
  );
};

MissionSection.propTypes = {
  mission: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    color: PropTypes.string,
    metrics: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        type: PropTypes.string,
        required: PropTypes.bool,
        multiline: PropTypes.bool,
      })
    ).isRequired,
  }).isRequired,
  selectedWeekData: PropTypes.object,
  onInputChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  weekId: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
};

function UserMission() {
  const navigate = useNavigate();
  const { id: targetUserId } = useParams();
  const [selectedWeek, setSelectedWeek] = useState("monthly");
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(true);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [submittedWeeks, setSubmittedWeeks] = useState([]);
  const [targetUserName, setTargetUserName] = useState("");

  const location = useLocation();
  const { userId, user_name, month, year, weekId, fromNotification } = location.state || {};

  const [selectedMonth, setSelectedMonth] = useState(
    month && !fromNotification ? month : moment().format("MMMM")
  );
  const [selectedYear, setSelectedYear] = useState(
    year && !fromNotification ? year : moment().format("YYYY")
  );

  // Initialize from notification state if coming from notification
  console.log(month, year);
  useEffect(() => {
    if (fromNotification && month && weekId) {
      // Parse month string like "January 2026" to set month and year
      const monthYearMatch = month.match(/([A-Za-z]+)\s+(\d{4})/);
      if (monthYearMatch) {
        const [, monthName, year] = monthYearMatch;
        setSelectedMonth(monthName);
        setSelectedYear(year);
      }

      // Set the week
      setSelectedWeek(weekId);
    }
  }, [fromNotification, month, weekId]);

  const weeks = useMemo(() => {
    return getWeeksForMonth(selectedMonth, selectedYear);
  }, [selectedMonth, selectedYear]);

  const displayMonthYear = `${selectedMonth} ${selectedYear}`;

  // Fetch current user data using react-query
  const { data: meData } = useQuery({
    queryKey: ["me"],
    queryFn: fetchUserProfile,
    retry: false,
  });

  const isAdmin = meData?.role_id === 1 || meData?.role_id === 2;

  const effectiveUserId = targetUserId || meData?.user_id || meData?.data?.user_id;

  console.log("UserMission Debug:", { targetUserId, meData, effectiveUserId });

  useEffect(() => {
    let isMounted = true;

    const fetchWeekData = async () => {
      console.log("fetchWeekData called. effectiveUserId:", effectiveUserId);

      if (!effectiveUserId) {
        console.log("effectiveUserId is missing, skipping fetch.");
        return; // Wait for effectiveUserId to be available
      }

      setLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/user-mission/get`, {
          params: {
            month: displayMonthYear,
            weekId: selectedWeek,
            userId: effectiveUserId,
            role: meData?.role,
          },
          withCredentials: true,
        });

        console.log("response", response);

        if (isMounted && response.data.status === 200) {
          const fetchedData =
            response.data.data && Object.keys(response.data.data).length > 0
              ? response.data.data
              : getDefaultFormData();

          setFormData((prev) => ({
            ...prev,
            [selectedWeek]: fetchedData,
          }));

          // If no data exists and NOT an admin viewing another user, allow immediate editing
          if (
            (!response.data.data || Object.keys(response.data.data).length === 0) &&
            !targetUserId
          ) {
            setIsEditing(true);
          } else {
            setIsEditing(false);
          }
        }
      } catch (error) {
        console.error("Error fetching week data:", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchWeekData();
    return () => {
      isMounted = false;
    };
  }, [selectedWeek, effectiveUserId, targetUserId]);

  useEffect(() => {
    const fetchSubmissionStatuses = async () => {
      if (!effectiveUserId) return;
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/user-mission/status`, {
          params: {
            month: displayMonthYear,
            userId: effectiveUserId,
          },
          withCredentials: true,
        });
        if (response.data.status === 200) {
          setSubmittedWeeks(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching submission statuses:", error);
      }
    };
    fetchSubmissionStatuses();
  }, [effectiveUserId, targetUserId, displayMonthYear]);

  const handleWeekChange = (event, newValue) => {
    setSelectedWeek(newValue);
  };

  const handleInputChange = (missionId, metricId, value, type) => {
    if (type === "number") {
      if (value !== "" && !/^-?\d*\.?\d*$/.test(value)) {
        return;
      }
    }

    setFormData((prev) => {
      const currentWeekData = { ...(prev[selectedWeek] || {}) };

      // Special handling for MPCD: Clear other fields if "Not Applicable" is selected
      if (missionId === "mpcd" && metricId === "is_applicable" && value === "Not Applicable") {
        const mpcdMission = MISSIONS.find((m) => m.id === "mpcd");
        mpcdMission.metrics.forEach((metric) => {
          if (metric.id !== "is_applicable") {
            delete currentWeekData[`mpcd_${metric.id}`];
          }
        });
      }

      return {
        ...prev,
        [selectedWeek]: {
          ...currentWeekData,
          [`${missionId}_${metricId}`]: value,
        },
      };
    });

    const errorKey = `${selectedWeek}_${missionId}_${metricId}`;
    if (errors[errorKey]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const handleBlur = (missionId, metricId, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    setFormData((prev) => ({
      ...prev,
      [selectedWeek]: {
        ...(prev[selectedWeek] || {}),
        [`${missionId}_${metricId}`]: trimmedValue,
      },
    }));
  };

  const validateCurrentWeek = () => {
    const newErrors = {};
    let isValid = true;

    MISSIONS.forEach((mission) => {
      const isApplicable = formData[selectedWeek]?.["mpcd_is_applicable"];

      mission.metrics.forEach((metric) => {
        // Conditional validation for MPCD
        if (mission.id === "mpcd" && metric.id !== "is_applicable") {
          if (isApplicable !== "Applicable") return; // Skip if not applicable or not selected
        }

        if (metric.required) {
          const val = formData[selectedWeek]?.[`${mission.id}_${metric.id}`];
          if (val === undefined || val === null || val.toString().trim() === "") {
            newErrors[`${selectedWeek}_${mission.id}_${metric.id}`] = "This field is required";
            isValid = false;
          }
        }
      });
    });

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateCurrentWeek()) {
      setNotification({
        open: true,
        message: "Please fill in all required fields.",
        severity: "error",
      });
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/user-mission/save`,
        {
          month: displayMonthYear,
          weekId: selectedWeek,
          data: formData[selectedWeek],
          userId: effectiveUserId,
        },
        {
          withCredentials: true,
        }
      );

      if (response.data.status === 200) {
        setNotification({
          open: true,
          message: "Success! Data has been updated.",
          severity: "success",
        });
        setIsEditing(false);
        // Update submittedWeeks if not already there
        setSubmittedWeeks((prev) => (prev.includes(selectedWeek) ? prev : [...prev, selectedWeek]));
      }
    } catch (error) {
      console.error("Error saving mission data:", error);
      setNotification({
        open: true,
        message: "Failed to save data. Please try again.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (
      window.confirm(
        `Clear all data for ${weeks.find((w) => w.id === selectedWeek).label} of ${displayMonthYear}?`
      )
    ) {
      setFormData((prev) => ({ ...prev, [selectedWeek]: getDefaultFormData() }));
      setIsEditing(true);
      setErrors((prev) => {
        const newErrors = { ...prev };
        Object.keys(newErrors).forEach((key) => {
          if (key.startsWith(selectedWeek)) delete newErrors[key];
        });
        return newErrors;
      });
    }
  };

  const toggleEdit = () => {
    setIsEditing(!isEditing);
  };

  const handleExportToExcel = async () => {
    setLoading(true);
    try {
      const allWeeksData = {};

      // Fetch data for all weeks of the selected month
      const fetchPromises = weeks.map((week) =>
        axios.get(`${process.env.REACT_APP_API_URL}/user-mission/get`, {
          params: {
            month: displayMonthYear,
            weekId: week.id,
            userId: effectiveUserId,
            role: meData?.role,
          },
          withCredentials: true,
        })
      );

      const results = await Promise.all(fetchPromises);
      results.forEach((response, index) => {
        const weekId = weeks[index].id;
        if (response.data.status === 200) {
          allWeeksData[weekId] = response.data.data || {};
        }
      });

      // Prepare Rows for Excel
      const excelRows = [];

      MISSIONS.forEach((mission) => {
        // Mission Header Row
        excelRows.push({
          "Mission/Category": mission.name.toUpperCase(),
          Metric: "",
          Status: "",
        });

        mission.metrics.forEach((metric) => {
          const row = {
            "Mission/Category": "",
            Metric: metric.label,
            Status: allWeeksData["monthly"]?.[`${mission.id}_${metric.id}`] ?? "-",
          };
          excelRows.push(row);
        });

        // Empty row for spacing
        excelRows.push({ "Mission/Category": "", Metric: "", Status: "" });
      });

      // Create Workbook and Worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Monthly Mission Report");

      // Style the header columns (optional/rudimentary with xlsx)
      const wscols = [
        { wch: 40 }, // Mission/Category
        { wch: 50 }, // Metric
        { wch: 25 }, // Status
      ];
      worksheet["!cols"] = wscols;

      // Generate Excel File
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const dataBlob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });

      saveAs(
        dataBlob,
        `Mission_Tracker_${displayMonthYear.replace(" ", "_")}_${user_name || "Report"}.xlsx`
      );

      setNotification({
        open: true,
        message: "Excel report generated successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Export Error:", error);
      setNotification({
        open: true,
        message: "Failed to generate Excel report.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <MDBox pb={4}>
        <Grid item xs={12}>
          <Card sx={{ overflow: "visible" }}>
            <MDBox
              mx={2}
              mt={2}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              gap={2}
            >
              <MDBox display="flex" alignItems="center" gap={2}>
                {/* <MDButton
                                    variant="outlined"
                                    color="white"
                                    size="small"
                                    onClick={() => navigate(-1)}
                                    sx={{
                                        minWidth: "40px",
                                        height: "40px",
                                        borderRadius: "50%",
                                        padding: 0,
                                        borderColor: "rgba(255,255,255,0.3)",
                                        "&:hover": {
                                            backgroundColor: "rgba(255,255,255,0.1)",
                                            borderColor: "white",
                                        }
                                    }}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>arrow_back</Icon> 
                                </MDButton> */}
                <MDBox>
                  <MDTypography variant="h5" color="white" fontWeight="medium">
                    {user_name && isAdmin ? ` ${user_name} ` : "Mission Tracker"}
                  </MDTypography>
                  <MDTypography variant="button" color="white" opacity={0.8}>
                    {displayMonthYear} – Monthly Mission Progress
                  </MDTypography>
                </MDBox>
              </MDBox>

              <MDBox display="flex" alignItems="center" gap={2}>
                <MDButton
                  variant="outlined"
                  onClick={handleExportToExcel}
                  disabled={loading}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <FileDownloadIcon fontSize="small" />
                  Export Excel
                </MDButton>
                {!isAdmin && loading && (
                  <MDBox display="flex" alignItems="center">
                    <CircularProgress size={20} color="white" />
                  </MDBox>
                )}
              </MDBox>
            </MDBox>

            <MDBox pt={4} px={3} pb={2}>
              {/* Month and Year Picker */}
              <MDBox mb={3} display="flex" justifyContent="center" gap={2}>
                <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }} required>
                  <InputLabel id="month-select-label">Month</InputLabel>
                  <Select
                    labelId="month-select-label"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    label="Month *"
                    sx={{ height: "40px" }}
                  >
                    {MONTHS.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }} required>
                  <InputLabel id="year-select-label">Year</InputLabel>
                  <Select
                    labelId="year-select-label"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    label="Year *"
                    sx={{ height: "40px" }}
                  >
                    {YEARS.map((y) => (
                      <MenuItem key={y} value={String(y)}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </MDBox>

              {/* Monthly Progress Status */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  width: "100%",
                  textAlign: "center",
                  bgcolor: submittedWeeks.includes("monthly") ? "#E8F5E9" : "#FFF3E0",
                  border: "1px solid",
                  borderColor: submittedWeeks.includes("monthly") ? "#C8E6C9" : "#FFE0B2",
                  borderRadius: "16px",
                }}
              >
                <MDTypography
                  variant="h6"
                  color={submittedWeeks.includes("monthly") ? "success" : "warning"}
                >
                  {submittedWeeks.includes("monthly")
                    ? "✓ Monthly Progress Submitted"
                    : "⚠ Monthly Progress Pending"}
                </MDTypography>
                <MDTypography variant="button" color="text">
                  For {displayMonthYear}
                </MDTypography>
                {submittedWeeks.includes("monthly") && !isAdmin && !isEditing && (
                  <MDBox mt={2}>
                    <MDButton variant="gradient" color="info" onClick={toggleEdit} size="small">
                      <Icon sx={{ mr: 1 }}>edit</Icon> Edit Monthly Report
                    </MDButton>
                  </MDBox>
                )}
              </Paper>
            </MDBox>

            <MDBox>
              {MISSIONS.map((mission) => (
                <MissionSection
                  key={mission.id}
                  mission={mission}
                  selectedWeekData={formData["monthly"] || {}}
                  onInputChange={handleInputChange}
                  onBlur={handleBlur}
                  errors={errors}
                  weekId="monthly"
                  readOnly={!isEditing && submittedWeeks.includes("monthly")}
                />
              ))}

              {isEditing && (
                <MDBox mt={4} display="flex" justifyContent="center" gap={2}>
                  <MDButton
                    variant="outlined"
                    color="error"
                    onClick={handleReset}
                    disabled={saving}
                  >
                    Reset
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="dark"
                    onClick={handleSubmit}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Submit Monthly Report"}
                  </MDButton>
                  {submittedWeeks.includes("monthly") && (
                    <MDButton variant="outlined" color="info" onClick={toggleEdit}>
                      Cancel
                    </MDButton>
                  )}
                </MDBox>
              )}
            </MDBox>
          </Card>
        </Grid>
      </MDBox>
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={notification.severity}
          variant="filled"
          sx={{ width: "100%", color: "white" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      <Footer />
    </DashboardLayout>
  );
}

export default UserMission;
